/* The active workspace, as a module-level source the HTTP clients and query
 * cache read at call time (same shape as auth/fetch.ts's authFetchInit). The
 * router's WorkspaceShell sets it — during render, before any child mounts —
 * so every gateway request and cache key made under /w/:workspaceId sees the
 * right id without threading it through provider constructors. */

import { AUTH_ENABLED } from "../auth/config";

let activeWorkspaceId: string | undefined;

export function setActiveWorkspaceId(id: string | undefined): void {
  activeWorkspaceId = id;
}

export function getActiveWorkspaceId(): string | undefined {
  return activeWorkspaceId;
}

/** Deployment-pinned workspace id (VITE_WORKSPACE_ID). Auth-less deployments
 *  (algo-dev) have no JWT and only the mock workspaces client-side, so the
 *  gateway header carries this fixed id — the same for every user. STRICTLY
 *  an auth-off mechanism: when the auth gate is on, users have real
 *  workspaces and a leaked pin would silently fetch another workspace's data
 *  under the one the UI shows. Read at call time so tests can vary
 *  window.__env__. */
function pinnedWorkspaceId(): string | undefined {
  if (typeof window === "undefined") return undefined;

  if (AUTH_ENABLED) return undefined;

  const value = window.__env__?.VITE_WORKSPACE_ID;
  return value && value.length > 0 ? value : undefined;
}

/** Spread into gateway fetch headers; empty until a workspace is resolved.
 *  A deployment-pinned id wins over the client-side active workspace (which
 *  is a mock in the deployments that pin one). */
export function workspaceHeader(): Record<string, string> {
  const id = pinnedWorkspaceId() ?? activeWorkspaceId;
  return id ? { "X-Workspace-Id": id } : {};
}

/** Prefix for useQuery cache keys, so switching workspaces changes every key
 *  (no stale cross-workspace data) and switching back within the dedupe window
 *  serves the previous workspace's cache instantly. */
export function workspaceKeyPrefix(): string {
  return activeWorkspaceId ? `ws:${activeWorkspaceId}|` : "";
}
